import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, ChevronRight, ChevronDown, AlertTriangle, Phone, User, ExternalLink, CheckCircle2, Clock, Cloud, FileText, AlertCircle, ClipboardList, Eye, Users, Lightbulb, Database, Shield, RotateCcw, Calendar, Building2, Download, Mail, Share2, Copy, MessageCircle, Sparkles, ShieldAlertIcon, Zap, Plus } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { Toast } from './Toast';
import { RiskLevel, RiskStatus, DataSource, CorrectiveAction, NearMissReport, PTPDocument, getRiskBand, RiskFactorScores, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, FactorSeverity } from './RiskAreasWidget';
import { EnrichedRiskArea } from '../types/prevention';
import { RiskAIChat } from './RiskAIChat';
import { PTPDocumentViewer } from './PTPDocumentViewer';
import { RiskScoreBreakdown } from './RiskScoreBreakdown';
import { usePreventionModel } from '../contexts/PreventionModelContext';
const STATUS_CONFIG: Record<RiskStatus, {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ElementType;
}> = {
  open: {
    label: 'Open',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    icon: AlertCircle
  },
  measures_taken: {
    label: 'Measures Taken',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: Clock
  },
  closed: {
    label: 'Closed',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    icon: CheckCircle2
  },
  monitoring: {
    label: 'Monitoring',
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    icon: Eye
  }
};
const RISK_LEVEL_CONFIG: Record<RiskLevel, {
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}> = {
  critical: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-300',
    label: 'Critical Risk'
  },
  high: {
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
    label: 'High Risk'
  },
  medium: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
    label: 'Medium Risk'
  },
  low: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    borderColor: 'border-emerald-300',
    label: 'Low Risk'
  }
};
const DATA_SOURCE_ICONS: Record<DataSource['type'], React.ReactNode> = {
  weather: <Cloud size={14} data-id="element-3131" data-mp="3u58s1dz0ayfd" />,
  inspection: <ClipboardList size={14} data-id="element-3132" data-mp="3u60p6t89547r" />,
  incident: <AlertCircle size={14} data-id="element-3133" data-mp="3u2wf5f83ve96" />,
  document: <FileText size={14} data-id="element-3134" data-mp="3p5cw820gqdaw" />,
  observation: <Eye size={14} data-id="element-3135" data-mp="3p4bbf5tnacfz" />,
  worker: <Users size={14} data-id="element-3136" data-mp="3p7qu0imj23m5" />,
  ptp: <ClipboardList size={14} data-id="element-3137" data-mp="3l4u3m5qqx37n" />
};
const PRIORITY_CONFIG: Record<CorrectiveAction['priority'] | 'high' | 'medium' | 'low', {
  label: string;
  color: string;
  bgColor: string;
}> = {
  immediate: {
    label: 'Immediate',
    color: 'text-red-700',
    bgColor: 'bg-red-100'
  },
  today: {
    label: 'Today',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100'
  },
  this_week: {
    label: 'This Week',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100'
  },
  high: {
    label: 'High',
    color: 'text-red-700',
    bgColor: 'bg-red-100'
  },
  medium: {
    label: 'Medium',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100'
  },
  low: {
    label: 'Low',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100'
  }
};
const SEVERITY_CONFIG: Record<NearMissReport['severity'], {
  color: string;
  bgColor: string;
  label: string;
}> = {
  high: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'High'
  },
  medium: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Medium'
  },
  low: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    label: 'Low'
  }
};
const INCIDENT_STATUS_CONFIG: Record<NearMissReport['status'], {
  color: string;
  bgColor: string;
  label: string;
}> = {
  open: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Open'
  },
  investigating: {
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    label: 'Investigating'
  },
  resolved: {
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    label: 'Resolved'
  }
};
interface RiskAreaDetailModalProps {
  riskArea: EnrichedRiskArea | null;
  onClose: () => void;
  onUpdateStatus: (areaId: string, newStatus: RiskStatus, notes: string, userName: string) => void;
  onOpenMethodology?: () => void;
}
function generateReportText(riskArea: EnrichedRiskArea): string {
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const band = getRiskBand(riskArea.riskScore);
  const lines: string[] = [];
  lines.push('═══════════════════════════════════════');
  lines.push('RISK AREA REPORT');
  lines.push('═══════════════════════════════════════');
  lines.push('');
  lines.push(`Risk Level: ${config.label.toUpperCase()}`);
  lines.push(`Risk Band: ${band.label} (${band.range})`);
  lines.push(`Status: ${statusConfig.label.toUpperCase()}`);
  lines.push(`Generated: ${new Date().toLocaleString()}`);
  lines.push('');
  lines.push('── CONTRIBUTING FACTORS ──');
  (Object.entries(riskArea.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).forEach(([factor, severity]) => {
    if (severity !== 'none') {
      lines.push(`• ${FACTOR_CONFIG[factor].label}: ${severity.toUpperCase()}`);
    }
  });
  lines.push('');
  lines.push('── LOCATION ──');
  lines.push(`Project: ${riskArea.project}`);
  lines.push(`Floor: ${riskArea.floor}`);
  lines.push(`Area: ${riskArea.location}`);
  lines.push(`Contractors: ${riskArea.contractors.join(', ')}`);
  lines.push('');
  lines.push('── RISK FACTORS ──');
  riskArea.riskFactors.forEach((factor, i) => {
    lines.push(`${i + 1}. ${factor}`);
  });
  lines.push('');
  lines.push('── DATA SOURCES ──');
  riskArea.dataSources.forEach(source => {
    lines.push(`• ${source.name}: ${source.description}`);
  });
  lines.push('');
  lines.push('── CORRECTIVE ACTIONS ──');
  riskArea.correctiveActions.forEach(action => {
    const priority = PRIORITY_CONFIG[action.priority];
    lines.push(`[${priority.label}] ${action.action}${action.assignedTo ? ` (Assigned: ${action.assignedTo})` : ''}`);
  });
  lines.push('');
  lines.push('── CONTACTS ──');
  riskArea.contacts.forEach(contact => {
    lines.push(`• ${contact.name} — ${contact.role}, ${contact.company} — ${contact.phone}`);
  });
  if (riskArea.measuresRecord) {
    lines.push('');
    lines.push('── MEASURES TAKEN ──');
    lines.push(`By: ${riskArea.measuresRecord.takenBy}`);
    lines.push(`Date: ${riskArea.measuresRecord.takenAt.toLocaleString()}`);
    lines.push(`Notes: ${riskArea.measuresRecord.notes}`);
  }
  if (riskArea.treatment) {
    lines.push('');
    lines.push('── CLOSURE RECORD ──');
    lines.push(`Closed by: ${riskArea.treatment.treatedBy}`);
    lines.push(`Date: ${riskArea.treatment.treatedAt.toLocaleString()}`);
    lines.push(`Notes: ${riskArea.treatment.notes}`);
  }
  lines.push('');
  lines.push('───────────────────────────────────────');
  lines.push(`Report ID: RISK-${riskArea.id} | Last updated: ${riskArea.lastUpdated.toLocaleString()}`);
  return lines.join('\n');
}
function generateReportHTML(riskArea: EnrichedRiskArea): string {
  const levelColors: Record<RiskLevel, string> = {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#d97706',
    low: '#059669'
  };
  const color = levelColors[riskArea.riskLevel];
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const band = getRiskBand(riskArea.riskScore);
  const css = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { border-bottom: 3px solid ${color}; padding-bottom: 20px; margin-bottom: 24px; }
    .header h1 { font-size: 24px; margin-bottom: 8px; }
    .badge { display: inline-block; background: ${color}; color: white; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; margin-right: 8px; }
    .score { display: inline-block; background: #f3f4f6; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; }
    .status { display: inline-block; padding: 4px 12px; border-radius: 4px; font-size: 13px; font-weight: 600; margin-left: 8px; border: 1px solid #e5e7eb; }
    .meta { color: #6b7280; font-size: 13px; margin-top: 8px; }
    .location { font-size: 16px; font-weight: 600; margin: 12px 0 4px; }
    .location-detail { color: #6b7280; font-size: 14px; }
    .section { margin-top: 24px; }
    .section h2 { font-size: 16px; font-weight: 700; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb; }
    .factor { padding: 8px 12px; background: #fef2f2; border-left: 3px solid ${color}; margin-bottom: 8px; font-size: 14px; border-radius: 0 4px 4px 0; }
    .source { padding: 8px 12px; background: #f9fafb; border: 1px solid #e5e7eb; margin-bottom: 6px; border-radius: 4px; font-size: 14px; }
    .source strong { display: block; font-size: 13px; }
    .source span { color: #6b7280; font-size: 12px; }
    .action { padding: 8px 12px; border: 1px solid #e5e7eb; margin-bottom: 6px; border-radius: 4px; font-size: 14px; display: flex; justify-content: space-between; align-items: center; }
    .action .priority { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 4px; }
    .priority-immediate { background: #fef2f2; color: #b91c1c; }
    .priority-today { background: #fff7ed; color: #c2410c; }
    .priority-this_week { background: #fffbeb; color: #b45309; }
    .contact { display: inline-block; padding: 8px 16px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; margin-right: 8px; margin-bottom: 8px; font-size: 13px; }
    .contact strong { display: block; }
    .contact .role { color: #6b7280; font-size: 12px; }
    .record { padding: 12px 16px; border-radius: 6px; margin-top: 16px; }
    .record-measures { background: #fffbeb; border: 1px solid #fde68a; }
    .record-closed { background: #ecfdf5; border: 1px solid #a7f3d0; }
    .record h3 { font-size: 14px; margin-bottom: 6px; }
    .record-measures h3 { color: #92400e; }
    .record-closed h3 { color: #065f46; }
    .record p { font-size: 13px; }
    .record-measures p { color: #b45309; }
    .record-closed p { color: #047857; }
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 12px; display: flex; justify-content: space-between; }
    @media print { body { padding: 20px; } }
  `;
  return `<!DOCTYPE html>
<html>
<head>
  <title>Risk Report — ${riskArea.project} ${riskArea.floor}</title>
  <style>{`;
  $;
  {
    css;
  }
  ;
  `}</style>
</head>
<body>
  <div class="header">
    <h1>Risk Area Report</h1>
    <div>
      <span class="badge">${config.label}</span>
      <span class="score">Band: ${band.label} (${band.range})</span>
      <span class="status">${statusConfig.label}</span>
    </div>
    <div class="location">${riskArea.project} › ${riskArea.floor} › ${riskArea.location}</div>
    <div class="location-detail">Contractors: ${riskArea.contractors.join(' · ')}</div>
    <div class="meta">Generated: ${new Date().toLocaleString()}</div>
  </div>

  <div class="section">
    <h2>Risk Factors</h2>
    ${riskArea.riskFactors.map((f, i) => `<div class="factor">${i + 1}. ${f}</div>`).join('')}
  </div>

  <div class="section">
    <h2>Data Sources</h2>
    ${riskArea.dataSources.map(s => `<div class="source"><strong>${s.name}</strong><span>${s.description}</span></div>`).join('')}
  </div>

  <div class="section">
    <h2>Corrective Actions</h2>
    ${riskArea.correctiveActions.map(a => `<div class="action"><span>${a.action}${a.assignedTo ? ` <em style="color:#6b7280">(${a.assignedTo})</em>` : ''}</span><span class="priority priority-${a.priority}">${PRIORITY_CONFIG[a.priority].label}</span></div>`).join('')}
  </div>

  <div class="section">
    <h2>Contractor Contacts</h2>
    ${riskArea.contacts.map(c => `<div class="contact"><strong>${c.name}</strong><span class="role">${c.role} · ${c.company}</span><br/>${c.phone}</div>`).join('')}
  </div>

  ${riskArea.measuresRecord ? `
  <div class="record record-measures">
    <h3>Measures Taken</h3>
    <p>${riskArea.measuresRecord.notes}</p>
    <p style="margin-top:6px;font-size:12px;opacity:0.8">By ${riskArea.measuresRecord.takenBy} · ${riskArea.measuresRecord.takenAt.toLocaleString()}</p>
  </div>` : ''}

  ${riskArea.treatment ? `
  <div class="record record-closed">
    <h3>Closure Record</h3>
    <p>${riskArea.treatment.notes}</p>
    <p style="margin-top:6px;font-size:12px;opacity:0.8">By ${riskArea.treatment.treatedBy} · ${riskArea.treatment.treatedAt.toLocaleString()}</p>
  </div>` : ''}

  <div class="footer">
    <span>Risk ID: RISK-${riskArea.id}</span>
    <span>Last updated: ${riskArea.lastUpdated.toLocaleString()}</span>
  </div>
</body>
</html>`;
}
export function RiskAreaDetailModal({
  riskArea,
  onClose,
  onUpdateStatus,
  onOpenMethodology
}: RiskAreaDetailModalProps) {
  useEscapeKey(onClose);
  const {
    addActionItem
  } = usePreventionModel();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [statusNotes, setStatusNotes] = useState('');
  const [activeForm, setActiveForm] = useState<'measures' | 'close' | 'reopen' | null>(null);
  const [formNotes, setFormNotes] = useState('');
  const [userName, setUserName] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [expandedWorkers, setExpandedWorkers] = useState<string | null>(null);
  const [selectedNearMissReport, setSelectedNearMissReport] = useState<NearMissReport | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [showGeneralFeedback, setShowGeneralFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('Risk assessment feedback');
  const [feedbackText, setFeedbackText] = useState('');
  const [selectedPTPDocument, setSelectedPTPDocument] = useState<PTPDocument | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);
  // State for Create Action Item
  const [creatingAction, setCreatingAction] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionOwner, setActionOwner] = useState('');
  const [actionDate, setActionDate] = useState('');
  const [actionPriority, setActionPriority] = useState<'high' | 'medium' | 'low'>('medium');
  const handleCreateAction = (e: React.FormEvent) => {
    e.preventDefault();
    if (!riskArea || !actionTitle || !actionOwner || !actionDate) return;
    addActionItem({
      title: actionTitle,
      description: `Action created from hotspot: ${riskArea.title}`,
      type: 'hotspot_mitigation',
      triggerSource: 'hotspot',
      triggerSourceId: riskArea.id,
      hotspotId: riskArea.id,
      owner: actionOwner,
      priority: actionPriority,
      dueDate: actionDate,
      status: 'open',
      rationale: 'Manually created to address hotspot risk.',
      expectedImpact: 'Mitigate identified risk factors.'
    });
    setCreatingAction(false);
    setActionTitle('');
    setActionOwner('');
    setActionDate('');
    setActionPriority('medium');
    // Show toast
    setToast({
      show: true,
      message: 'Action item created successfully',
      type: 'success'
    });
  };
  const shareMenuRef = useRef<HTMLDivElement>(null);
  // --- NEW STATE FOR PROGRESSIVE DISCLOSURE ---
  const [reviewedSections, setReviewedSections] = useState<Set<string>>(new Set());
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const toggleSection = (section: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);else next.add(section);
      return next;
    });
    setReviewedSections(prev => new Set(prev).add(section));
  };
  // --------------------------------------------
  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);
  if (!riskArea) return null;
  const config = RISK_LEVEL_CONFIG[riskArea.riskLevel];
  const statusConfig = STATUS_CONFIG[riskArea.status];
  const StatusIcon = statusConfig.icon;
  // --- COMPUTED VALUES FOR QUICK OVERVIEW ---
  const activeFactors = (Object.entries(riskArea.riskFactorScores) as [keyof RiskFactorScores, FactorSeverity][]).filter(([_, sev]) => sev !== 'none').sort((a, b) => {
    const order = {
      high: 0,
      medium: 1,
      low: 2,
      none: 3
    };
    return order[a[1]] - order[b[1]];
  });
  const topFactor = activeFactors[0];
  const sortedActions = [...riskArea.correctiveActions].sort((a, b) => {
    const order = {
      immediate: 0,
      today: 1,
      this_week: 2
    };
    return order[a.priority] - order[b.priority];
  });
  const topAction = sortedActions[0];
  const allReviewed = reviewedSections.size === 5;
  const remainingToReview = 5 - reviewedSections.size;
  // ------------------------------------------
  const resetForm = () => {
    setActiveForm(null);
    setFormNotes('');
    setUserName('');
  };
  const handleSubmitStatusUpdate = (newStatus: RiskStatus) => {
    if (!formNotes.trim() || !userName.trim()) return;
    onUpdateStatus(riskArea.id, newStatus, formNotes, userName);
    let msg = '';
    if (newStatus === 'measures_taken') msg = 'Measures recorded successfully';else if (newStatus === 'closed') msg = 'Risk area marked as closed';else if (newStatus === 'open') msg = 'Risk area reopened';
    setToastMessage(msg);
    setShowToast(true);
    resetForm();
  };
  const handleContactClick = (phone: string, name: string) => {
    setToastMessage(`Calling ${name}...`);
    setShowToast(true);
  };
  const handleDataSourceClick = (source: DataSource) => {
    if (source.type === 'weather' && source.link) {
      window.open(source.link, '_blank', 'noopener,noreferrer');
    } else if (source.type === 'incident' && source.nearMissReports && source.nearMissReports.length > 0) {
      setSelectedNearMissReport(source.nearMissReports[0]);
    } else if (source.type === 'worker' && source.workers) {
      setExpandedWorkers(expandedWorkers === source.id ? null : source.id);
    } else if (source.type === 'ptp' && source.ptpDocument) {
      setSelectedPTPDocument(source.ptpDocument);
    }
  };
  const isClickableSource = (source: DataSource) => {
    return source.type === 'weather' && source.link || source.type === 'incident' && source.nearMissReports && source.nearMissReports.length > 0 || source.type === 'worker' && source.workers && source.workers.length > 0 || source.type === 'ptp' && !!source.ptpDocument;
  };
  const handleDownloadPDF = () => {
    const html = generateReportHTML(riskArea);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 300);
    }
    setShowShareMenu(false);
    setToastMessage('Report opened for printing/PDF download');
    setShowToast(true);
  };
  const handleShareEmail = () => {
    setShowEmailForm(true);
    setShowShareMenu(false);
  };
  const handleSendEmail = () => {
    const reportText = generateReportText(riskArea);
    const subject = encodeURIComponent(`Risk Report: ${riskArea.project} — ${riskArea.floor} — ${riskArea.location} [${RISK_LEVEL_CONFIG[riskArea.riskLevel].label}]`);
    const body = encodeURIComponent(`${emailNote ? emailNote + '\n\n---\n\n' : ''}${reportText}`);
    const mailto = `mailto:${encodeURIComponent(emailTo)}?subject=${subject}&body=${body}`;
    window.open(mailto);
    setShowEmailForm(false);
    setEmailTo('');
    setEmailNote('');
    setToastMessage('Email client opened with report');
    setShowToast(true);
  };
  const handleCopyReport = () => {
    const reportText = generateReportText(riskArea);
    navigator.clipboard.writeText(reportText).then(() => {
      setToastMessage('Report copied to clipboard');
      setShowToast(true);
    }).catch(() => {
      setToastMessage('Failed to copy — try again');
      setShowToast(true);
    });
    setShowShareMenu(false);
  };
  const submitGeneralFeedback = () => {
    if (!feedbackText.trim()) return;
    setToastMessage('Feedback submitted. Thank you!');
    setShowToast(true);
    setShowGeneralFeedback(false);
    setFeedbackText('');
  };
  return <>
      <AnimatePresence data-id="element-3138" data-mp="2pljqip6pk37">
        {riskArea && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-3139" data-mp="2qeoje4l1zip">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-3140" data-mp="3k3qrn1s7hxdr" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5,
          bounce: 0.3
        }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-3141" data-mp="1lp2q7xucs1wj">
              {/* SIF Banner */}
              {riskArea.sifPotential && <div className="bg-red-800 text-white px-6 py-4 flex items-center gap-3 flex-shrink-0 animate-pulse" data-id="element-3142" data-mp="k6isxga5lgha">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0" data-id="element-3143" data-mp="f7fx8mg2cmlz">
                    <ShieldAlertIcon size={22} data-id="element-3144" data-mp="2qwcc0jliij7j" />
                  </div>
                  <div data-id="element-3145" data-mp="2vtmbyt6j2d9m">
                    <span className="font-black text-sm uppercase tracking-wider block" data-id="element-3146" data-mp="2vuxekpo8lez5">
                      ⚠️ SIF Potential — Serious Injury or Fatality Risk
                    </span>
                    <span className="text-red-200 text-xs" data-id="element-3147" data-mp="34nhgq03qm1oq">
                      This hotspot has been flagged for potential
                      life-threatening outcomes. Prioritize immediate review.
                    </span>
                  </div>
                </div>}

              {/* Header */}
              <div className={`p-6 border-b ${config.borderColor} ${config.bgColor}`} data-id="element-3148" data-mp="3jk87jkzci52q">
                <div className="flex items-start justify-between" data-id="element-3149" data-mp="3nj7jvqnep447">
                  <div className="flex-1" data-id="element-3150" data-mp="1p70jcc5vkrq2">
                    <div className="flex items-center gap-3 mb-2" data-id="element-3151" data-mp="1p81wcm6gboaf">
                      <div className="flex items-center gap-1 bg-white/80 px-2.5 py-1 rounded-full" data-id="element-3152" data-mp="1t7srt36xasla">
                        <AlertTriangle size={14} className={config.color} data-id="element-3153" data-mp="xvjbvrxt7z3l" />
                        <span className={`text-sm font-bold ${config.color}`} data-id="element-3154" data-mp="tuh9514op4wv">
                          {getRiskBand(riskArea.riskScore).label} (
                          {getRiskBand(riskArea.riskScore).range})
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/50 bg-white/80 ${statusConfig.color}`} data-id="element-3155" data-mp="f75w0usutcun">
                        <StatusIcon size={14} data-id="element-3156" data-mp="oy0jg2hu6iho" />
                        <span className="text-sm font-semibold" data-id="element-3157" data-mp="k5zbutvm5fxz">
                          {statusConfig.label}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 text-lg font-bold text-gray-900 mb-1" data-id="element-3158" data-mp="3nmfu7rtmhxx5">
                      <MapPin size={18} className="text-gray-500" data-id="element-3159" data-mp="234dmrnsil752" />
                      <span data-id="element-3160" data-mp="2332mvz26wjj8">{riskArea.project}</span>
                      <ChevronRight size={16} className="text-gray-400" data-id="element-3161" data-mp="27vdmf0t0q3b9" />
                      <span className="text-gray-700" data-id="element-3162" data-mp="1u3pdf6e2fuyi">{riskArea.floor}</span>
                      <ChevronRight size={16} className="text-gray-400" data-id="element-3163" data-mp="1u1mchrbdkhf0" />
                      <span className="text-gray-600 font-medium" data-id="element-3164" data-mp="1y63r7c2vo4ix">
                        {riskArea.location}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-3165" data-mp="2quhsc0p9jgyp">
                      <Users size={14} data-id="element-3166" data-mp="2cugrumcwubjr" />
                      <span data-id="element-3167" data-mp="2ctf5vmucw4n9">{riskArea.contractors.join(' · ')}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1" data-id="element-3168" data-mp="2hrgns4g1pxx5">
                    {/* Share/Report Button */}
                    <div className="relative" ref={shareMenuRef} data-id="element-3169" data-mp="11h4k40e158sb">
                      <button onClick={() => setShowShareMenu(!showShareMenu)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors" title="Generate report" data-id="element-3170" data-mp="16ewbmtarisvz">
                        <Share2 size={18} data-id="element-3171" data-mp="2ycfpr6u671lk" />
                      </button>

                      <AnimatePresence data-id="element-3172" data-mp="2ucfbpnazgsf1">
                        {showShareMenu && <motion.div initial={{
                      opacity: 0,
                      y: -8,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: -8,
                      scale: 0.95
                    }} transition={{
                      duration: 0.15
                    }} className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-3173" data-mp="2pcj65jmn0sxd">
                            <button onClick={handleDownloadPDF} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3174" data-mp="25aoj7h94jzdx">
                              <Download size={16} className="text-gray-400" data-id="element-3175" data-mp="1dlmlfebnmsmt" />
                              <div data-id="element-3176" data-mp="1hjkamh5fdeq7">
                                <p className="font-medium" data-id="element-3177" data-mp="1mk6v5pz8lryw">Download PDF</p>
                                <p className="text-xs text-gray-500" data-id="element-3178" data-mp="1mlhwzk94gpnd">
                                  Print or save as PDF
                                </p>
                              </div>
                            </button>
                            <button onClick={handleShareEmail} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3179" data-mp="29mjub87zbuic">
                              <Mail size={16} className="text-gray-400" data-id="element-3180" data-mp="2ih7w2oeciu2e" />
                              <div data-id="element-3181" data-mp="2dmu1go7icqys">
                                <p className="font-medium" data-id="element-3182" data-mp="2sdb0y20l16t1">Share via Email</p>
                                <p className="text-xs text-gray-500" data-id="element-3183" data-mp="2sfdzxbjpqa9w">
                                  Send report to a recipient
                                </p>
                              </div>
                            </button>
                            <button onClick={handleCopyReport} className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-3" data-id="element-3184" data-mp="2xbuno4n02mmg">
                              <Copy size={16} className="text-gray-400" data-id="element-3185" data-mp="130popdmuyydg" />
                              <div data-id="element-3186" data-mp="sv7x0dkjgqqv">
                                <p className="font-medium" data-id="element-3187" data-mp="wvru5h543ud0">Copy to Clipboard</p>
                                <p className="text-xs text-gray-500" data-id="element-3188" data-mp="wxui7vmpfdf3">
                                  Copy report as text
                                </p>
                              </div>
                            </button>
                          </motion.div>}
                      </AnimatePresence>
                    </div>

                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded-lg transition-colors" data-id="element-3189" data-mp="e6bgds4f25en">
                      <X size={20} data-id="element-3190" data-mp="55dlbzbyh887" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Email Form Banner */}
              <AnimatePresence data-id="element-3191" data-mp="1zj4rgov2t9yw">
                {showEmailForm && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="border-b border-gray-200 bg-blue-50 overflow-hidden" data-id="element-3192" data-mp="31aj0tw0pavzo">
                    <div className="p-4 space-y-3" data-id="element-3193" data-mp="3p03hg89dth5s">
                      <div className="flex items-center gap-2 mb-1" data-id="element-3194" data-mp="3oysjip2mb3ml">
                        <Mail size={16} className="text-blue-600" data-id="element-3195" data-mp="3tywyganvnqap" />
                        <h4 className="font-semibold text-gray-900" data-id="element-3196" data-mp="3txc009yfzn7k">
                          Share Report via Email
                        </h4>
                      </div>
                      <div data-id="element-3197" data-mp="1zn00rzyudo77">
                        <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3198" data-mp="24k8o0eyukifb">
                          Recipient Email
                        </label>
                        <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="name@company.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-3199" data-mp="lqp20e2509mv" />
                      </div>
                      <div data-id="element-3200" data-mp="10mo5tci7rnm7">
                        <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3201" data-mp="10ng2kqnjrre2">
                          Add a Note (optional)
                        </label>
                        <textarea value={emailNote} onChange={e => setEmailNote(e.target.value)} placeholder="Hi, please review this risk area report..." rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-3202" data-mp="qoh0jqxaeu01" />
                      </div>
                      <div className="flex gap-2" data-id="element-3203" data-mp="33ux6ebtecj47">
                        <button onClick={() => {
                    setShowEmailForm(false);
                    setEmailTo('');
                    setEmailNote('');
                  }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-3204" data-mp="33u5at0zsuldx">
                          Cancel
                        </button>
                        <button onClick={handleSendEmail} disabled={!emailTo.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-3205" data-mp="2p72daubh8cql">
                          <Mail size={14} data-id="element-3206" data-mp="1rkprigaza5ku" />
                          Open Email Client
                        </button>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-3207" data-mp="2fiewvdenhyvo">
                {/* Status Records Display */}
                {riskArea.status === 'measures_taken' && riskArea.measuresRecord && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-3208" data-mp="2p8rdwvn9b9x2">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3209" data-mp="rkxfqvq19vf1">
                        <Clock size={18} className="text-amber-600" data-id="element-3210" data-mp="rijgsbs8fz27" />
                        <h3 className="font-semibold text-amber-900" data-id="element-3211" data-mp="wi5wqszg3tep">
                          Measures Taken
                        </h3>
                      </div>
                      <p className="text-sm text-amber-800 mb-2" data-id="element-3212" data-mp="16a1yqf4nk3o7">
                        {riskArea.measuresRecord.notes}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-amber-600" data-id="element-3213" data-mp="8nmcz1570juu">
                        <span className="flex items-center gap-1" data-id="element-3214" data-mp="dlef0nzjxmcr">
                          <User size={12} data-id="element-3215" data-mp="ijpp8ztrdyvi" />
                          {riskArea.measuresRecord.takenBy}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-3216" data-mp="mmvuc454xk0e">
                          <Clock size={12} data-id="element-3217" data-mp="1tzwa0s9bpk6c" />
                          {riskArea.measuresRecord.takenAt.toLocaleString()}
                        </span>
                      </div>
                    </motion.div>}

                {riskArea.status === 'closed' && riskArea.treatment && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-id="element-3218" data-mp="3ei854d80860y">
                    <div className="flex items-center gap-2 mb-2" data-id="element-3219" data-mp="2utfsaepfexjq">
                      <Shield size={18} className="text-emerald-600" data-id="element-3220" data-mp="2uvagq2zwp7sk" />
                      <h3 className="font-semibold text-emerald-900" data-id="element-3221" data-mp="2qsdv03f2kvke">
                        Risk Closed
                      </h3>
                    </div>
                    <p className="text-sm text-emerald-800 mb-2" data-id="element-3222" data-mp="ozxyf47ygh7d">
                      {riskArea.treatment.notes}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-emerald-600" data-id="element-3223" data-mp="k2fyg3bx1cbi">
                      <span className="flex items-center gap-1" data-id="element-3224" data-mp="134osfxjdrv14">
                        <User size={12} data-id="element-3225" data-mp="17w7ypv433t61" />
                        {riskArea.treatment.treatedBy}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-3226" data-mp="u5uspyar6ojc">
                        <Clock size={12} data-id="element-3227" data-mp="z46267x4yx11" />
                        {riskArea.treatment.treatedAt.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>}

                {/* Reopen History */}
                {riskArea.reopenHistory && riskArea.reopenHistory.length > 0 && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-3228" data-mp="1cv1a9hzt9a3k">
                      <div className="flex items-center gap-2 mb-2" data-id="element-3229" data-mp="1htcl9w1udqnd">
                        <RotateCcw size={16} className="text-gray-600" data-id="element-3230" data-mp="1futog2xo5oe" />
                        <h4 className="text-sm font-semibold text-gray-900" data-id="element-3231" data-mp="6fh7orql5py9">
                          Reopen History
                        </h4>
                      </div>
                      <div className="space-y-2" data-id="element-3232" data-mp="3uzj0qs8zv6lb">
                        {riskArea.reopenHistory.map((record, index) => <div key={index} className="text-xs text-gray-700 bg-white border border-gray-100 rounded p-2" data-id="element-3233" data-mp="7wny9xzm6g9b" data-mpi={index}>
                            <p className="font-medium" data-id="element-3234" data-mp="3bdcy714oyrq0">{record.reason}</p>
                            <p className="text-gray-500 mt-1" data-id="element-3235" data-mp="3bba9ckf9z4mv">
                              Reopened by {record.reopenedBy} ·{' '}
                              {record.reopenedAt.toLocaleString()}
                            </p>
                          </div>)}
                      </div>
                    </div>}

                {/* Quick Overview Card */}
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 shadow-sm" data-id="element-3236" data-mp="2sksgmfug091i">
                  <div className="flex items-center gap-2 mb-4" data-id="element-3237" data-mp="2nlpmvrjwxp8u">
                    <Zap size={18} className="text-indigo-600" data-id="element-3238" data-mp="233a2t6l0hgqc" />
                    <h3 className="font-bold text-indigo-900" data-id="element-3239" data-mp="273akr17bka25">
                      Quick Overview
                    </h3>
                  </div>

                  <div className="space-y-4" data-id="element-3240" data-mp="2c12bhrw89q4r">
                    {topFactor && <div data-id="element-3241" data-mp="2h2j6j6s16idf">
                        <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wider mb-1.5" data-id="element-3242" data-mp="2gznajnzqu3rm">
                          Primary Risk Driver
                        </p>
                        <div className="flex items-center gap-2 mb-1" data-id="element-3243" data-mp="2q0lfq9lx4hbs">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${FACTOR_SEVERITY_COLORS[topFactor[1]].bg} ${FACTOR_SEVERITY_COLORS[topFactor[1]].text}`} data-id="element-3244" data-mp="2uu64db1mfyyb">
                            {FACTOR_CONFIG[topFactor[0]].short}
                          </span>
                          <p className="text-sm font-bold text-gray-900" data-id="element-3245" data-mp="39qngi4hmxhlx">
                            {FACTOR_CONFIG[topFactor[0]].label}
                          </p>
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-3246" data-mp="t2ntke0wsyo6">
                          {riskArea.riskFactors[0]}
                        </p>
                      </div>}

                    {topAction && <div className="pt-4 border-t border-indigo-100/50" data-id="element-3247" data-mp="1gtjtjyfir06i">
                        <p className="text-xs font-semibold text-indigo-800 uppercase tracking-wider mb-1.5" data-id="element-3248" data-mp="1gss0au9e9eje">
                          Top Corrective Action
                        </p>
                        <div className="flex items-start gap-2" data-id="element-3249" data-mp="1725ozc8pna6i">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold mt-0.5 ${PRIORITY_CONFIG[topAction.priority].bgColor} ${PRIORITY_CONFIG[topAction.priority].color}`} data-id="element-3250" data-mp="125f9or4qcim5">
                            {PRIORITY_CONFIG[topAction.priority].label}
                          </span>
                          <p className="text-sm font-medium text-gray-900" data-id="element-3251" data-mp="3q9f9re08h8qa">
                            {topAction.action}
                          </p>
                        </div>
                      </div>}

                    <div className="pt-4 border-t border-indigo-100/50 flex items-center gap-6" data-id="element-3252" data-mp="3bipv21nkdbho">
                      <div className="flex items-center gap-1.5" data-id="element-3253" data-mp="37gl5pf45f10u">
                        <Clock size={14} className="text-indigo-400" data-id="element-3254" data-mp="37erwbs25i2p5" />
                        <span className="text-xs font-medium text-indigo-800" data-id="element-3255" data-mp="32gysh5oblks1">
                          Updated{' '}
                          {riskArea.lastUpdated.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5" data-id="element-3256" data-mp="qrpgq10g743o">
                        <Database size={14} className="text-indigo-400" data-id="element-3257" data-mp="qqeck033buc1" />
                        <span className="text-xs font-medium text-indigo-800 capitalize" data-id="element-3258" data-mp="z8q0gur7dxln">
                          {riskArea.dataConfidence} confidence
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Collapsible Sections */}
                <div className="space-y-3" data-id="element-3259" data-mp="1d2gzh97b5z34">
                  {/* Section 1: Risk Factors */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3260" data-mp="gcq7bgmhoo0h">
                    <button onClick={() => toggleSection('factors')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3261" data-mp="kds6xykbj22z">
                      <div className="flex items-center gap-3" data-id="element-3262" data-mp="20r0cg21az01j">
                        <AlertTriangle size={18} className="text-gray-500" data-id="element-3263" data-mp="25pbhlzgnkmdf" />
                        <h3 className="font-bold text-gray-900" data-id="element-3264" data-mp="25rek3iyzs7z1">
                          All Risk Factors
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3265" data-mp="1b11nxil2wncl">
                        {reviewedSections.has('factors') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3266" data-mp="1pykhqzcm5qqw" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3267" data-mp="xay571lerbrw" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('factors') ? 'rotate-180' : ''}`} data-id="element-3268" data-mp="s9h87h61o9gl" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3269" data-mp="eim22cmxk6gp">
                      {openSections.has('factors') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3270" data-mp="9kkkjw55kf7c">
                          <div className="p-5 border-t border-gray-200 space-y-6" data-id="element-3271" data-mp="33yxg7r6lggvh">
                            <RiskScoreBreakdown scores={riskArea.riskFactorScores} riskScore={riskArea.riskScore} onTuneWeights={onOpenMethodology} data-id="element-3272" data-mp="33wudc6kcet9c" />
                            <div data-id="element-3273" data-mp="2yyj4nz5saosp">
                              <h4 className="font-bold text-gray-900 mb-3" data-id="element-3274" data-mp="2z1ow3l1vy2rl">
                                Why This Area is Risky
                              </h4>
                              <div className="space-y-2" data-id="element-3275" data-mp="1nlgsdgapwlec">
                                {riskArea.riskFactors.map((factor, index) => <div key={index} className="flex items-start gap-3 p-3 bg-red-50 border border-red-100 rounded-lg" data-id="element-3276" data-mp="23tmt19wy1ohf" data-mpi={index}>
                                    <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3277" data-mp="1p3yuggg5d3xe">
                                      <span className="text-xs font-bold text-red-600" data-id="element-3278" data-mp="2mpwggntqjre3">
                                        {index + 1}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-800" data-id="element-3279" data-mp="28qws7ha4xeig">
                                      {factor}
                                    </p>
                                  </div>)}
                              </div>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 2: Data Sources */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3280" data-mp="3hzrl17r5dfvq">
                    <button onClick={() => toggleSection('dataSources')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3281" data-mp="3mq83555wxpof">
                      <div className="flex items-center gap-3" data-id="element-3282" data-mp="hqbeaxi5z1l">
                        <Database size={18} className="text-gray-500" data-id="element-3283" data-mp="4kda8vslck8a" />
                        <h3 className="font-bold text-gray-900" data-id="element-3284" data-mp="4j210ggdmk9s">
                          Data Sources
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3285" data-mp="26dsyxn54bp0m">
                        {reviewedSections.has('dataSources') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3286" data-mp="2bd5wp2xj2ghz" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3287" data-mp="39p8jbjiozzlp" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('dataSources') ? 'rotate-180' : ''}`} data-id="element-3288" data-mp="3intwt09b4ofm" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3289" data-mp="3sjx5koynzlbj">
                      {openSections.has('dataSources') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3290" data-mp="a9v17zq8z52n">
                          <div className="p-5 border-t border-gray-200 space-y-2" data-id="element-3291" data-mp="13sjffv246gn5">
                            {riskArea.dataSources.map((source, index) => {
                        const isClickable = isClickableSource(source);
                        const isExpanded = expandedWorkers === source.id;
                        return <div key={source.id} data-id="element-3292" data-mp="g0ceeilg1lhe" data-mpi={index}>
                                  <button onClick={() => isClickable && handleDataSourceClick(source)} disabled={!isClickable} className={`w-full flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-left transition-colors ${isClickable ? 'hover:bg-gray-100 hover:border-gray-300 cursor-pointer' : 'cursor-default'}`} data-id="element-3293" data-mp="ku75w87aj4r1">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600" data-id="element-3294" data-mp="cmm7kn39r71q">
                                      {DATA_SOURCE_ICONS[source.type]}
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-3295" data-mp="3ur7bcwrpuend">
                                      <p className="text-sm font-medium text-gray-900 flex items-center gap-1" data-id="element-3296" data-mp="3usif4wl5vmeh">
                                        {source.name}
                                        {source.type === 'weather' && source.link && <ExternalLink size={12} className="text-blue-500" data-id="element-3297" data-mp="3g44feuj1ke25" />}
                                        {source.type === 'incident' && source.nearMissReports && <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded ml-1" data-id="element-3298" data-mp="1qn0hl66iwqyp">
                                              {source.nearMissReports.length}{' '}
                                              reports
                                            </span>}
                                        {source.type === 'ptp' && source.ptpDocument && <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded ml-1" data-id="element-3299" data-mp="1csg4f5p4qxp2">
                                              View Document
                                            </span>}
                                        {source.type === 'worker' && source.workers && <ChevronDown size={14} className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-3300" data-mp="3uxubria1hzag" />}
                                      </p>
                                      <p className="text-xs text-gray-500" data-id="element-3301" data-mp="3c7l0liw9yx6m">
                                        {source.description}
                                      </p>
                                    </div>
                                  </button>
                                  <AnimatePresence data-id="element-3302" data-mp="379jip1al53wq">
                                    {isExpanded && source.workers && <motion.div initial={{
                              opacity: 0,
                              height: 0
                            }} animate={{
                              opacity: 1,
                              height: 'auto'
                            }} exit={{
                              opacity: 0,
                              height: 0
                            }} className="mt-2 ml-11 space-y-2" data-id="element-3303" data-mp="2tbmjdx4qyjed">
                                        {source.workers.map(worker => <div key={worker.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-3304" data-mp="37e37n58i261w">
                                            <div className="flex items-start justify-between" data-id="element-3305" data-mp="3bf6n3qmmy02l">
                                              <div data-id="element-3306" data-mp="291qrir32zxnq">
                                                <p className="font-semibold text-gray-900" data-id="element-3307" data-mp="2dzk0tszol7s8">
                                                  {worker.name}
                                                </p>
                                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1" data-id="element-3308" data-mp="2mrlyxkma2bzc">
                                                  <Building2 size={12} data-id="element-3309" data-mp="2rrqd33zpqume" />
                                                  <span data-id="element-3310" data-mp="2wprwxr4yqxyx">
                                                    {worker.contractor}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1" data-id="element-3311" data-mp="35pezwhdccbu3">
                                                  <Calendar size={12} data-id="element-3312" data-mp="17cwt5aqjqabj" />
                                                  <span data-id="element-3313" data-mp="1bdqhg57whr4y">
                                                    First day:{' '}
                                                    {worker.firstDayOnSite.toLocaleDateString()}{' '}
                                                    ({worker.daysOnSite} days on
                                                    site)
                                                  </span>
                                                </div>
                                              </div>
                                              <a href={`tel:${worker.phone}`} className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors" onClick={e => e.stopPropagation()} data-id="element-3314" data-mp="1mr8zj8p3bdv1">
                                                <Phone size={14} data-id="element-3315" data-mp="1l9jrqa1fnsd8" />
                                                {worker.phone}
                                              </a>
                                            </div>
                                          </div>)}
                                      </motion.div>}
                                  </AnimatePresence>
                                </div>;
                      })}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 3: Corrective Actions */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3316" data-mp="200ixzeesq4tn">
                    <button onClick={() => toggleSection('actions')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3317" data-mp="ojj7moc0e029">
                      <div className="flex items-center gap-3" data-id="element-3318" data-mp="bzvo97icchcj">
                        <Lightbulb size={18} className="text-gray-500" data-id="element-3319" data-mp="q0oidr4qdaf4" />
                        <h3 className="font-bold text-gray-900" data-id="element-3320" data-mp="pylx1keiw5gq">
                          Suggested Corrective Actions
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3321" data-mp="3ti6p1v8robdy">
                        {reviewedSections.has('actions') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3322" data-mp="3og93uvgt36id" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3323" data-mp="291180x9tdq1" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('actions') ? 'rotate-180' : ''}`} data-id="element-3324" data-mp="3aoktawm7gq3i" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3325" data-mp="1ely2epssja77">
                      {openSections.has('actions') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3326" data-mp="1jfaq8xpnbcyw">
                          <div className="p-5 border-t border-gray-200 space-y-4" data-id="element-3327" data-mp="14pd04c85od72">
                            <div className="space-y-2" data-id="element-3328" data-mp="14n8o1703s7vp">
                              {riskArea.correctiveActions.map((action, index) => {
                          const priorityConfig = PRIORITY_CONFIG[action.priority];
                          return <div key={action.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg" data-id="element-3329" data-mp="2lan97zzdvkcs" data-mpi={index}>
                                      <input type="checkbox" className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-id="element-3330" data-mp="27i7cfgx2sikb" />
                                      <div className="flex-1" data-id="element-3331" data-mp="1xkjeki46auxx">
                                        <p className="text-sm text-gray-900" data-id="element-3332" data-mp="1tj7oyclau9pq">
                                          {action.action}
                                        </p>
                                        {action.assignedTo && <p className="text-xs text-gray-500 mt-0.5" data-id="element-3333" data-mp="3nucnx3lfxxyq">
                                            Assigned to: {action.assignedTo}
                                          </p>}
                                      </div>
                                      <span className={`text-xs font-semibold px-2 py-1 rounded ${priorityConfig.bgColor} ${priorityConfig.color}`} data-id="element-3334" data-mp="19rg4nv5dp7w">
                                        {priorityConfig.label}
                                      </span>
                                    </div>;
                        })}
                            </div>

                            <div className="pt-2 border-t border-gray-100" data-id="element-3335" data-mp="3jhqng93zvc2k">
                              {!creatingAction ? <button onClick={() => setCreatingAction(true)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5" data-id="element-3336" data-mp="1hnzp1fmzw012">
                                  <Plus size={16} data-id="element-3337" data-mp="1mrab07v2vntr" /> Create Custom Action Item
                                </button> : <form onSubmit={handleCreateAction} className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3" data-id="element-3338" data-mp="1vt00h85egcow">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-3339" data-mp="25lz0dm6cjpnt">
                                    New Action Item
                                  </h4>
                                  <div data-id="element-3340" data-mp="2fikaun14xsz1">
                                    <input type="text" required value={actionTitle} onChange={e => setActionTitle(e.target.value)} placeholder="Action description..." className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3341" data-mp="p4tev5y2zg9l" />
                                  </div>
                                  <div className="grid grid-cols-3 gap-3" data-id="element-3342" data-mp="30p83nvpf0b64">
                                    <div data-id="element-3343" data-mp="30rcgj35akkij">
                                      <input type="text" required value={actionOwner} onChange={e => setActionOwner(e.target.value)} placeholder="Assignee" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3344" data-mp="3epivb0yjttsc" />
                                    </div>
                                    <div data-id="element-3345" data-mp="shd2pnji5twq">
                                      <input type="date" required value={actionDate} onChange={e => setActionDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3346" data-mp="si4vkqlptdjb" />
                                    </div>
                                    <div data-id="element-3347" data-mp="23ymhvxvllzs8">
                                      <select value={actionPriority} onChange={e => setActionPriority(e.target.value as any)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3348" data-mp="2dt0hgwv40r62">
                                        <option value="high" data-id="element-3349" data-mp="1l3l5o4vmeu93">
                                          High Priority
                                        </option>
                                        <option value="medium" data-id="element-3350" data-mp="1h0oie0v4y9yt">
                                          Medium Priority
                                        </option>
                                        <option value="low" data-id="element-3351" data-mp="174tt4mwepgn2">
                                          Low Priority
                                        </option>
                                      </select>
                                    </div>
                                  </div>
                                  <div className="flex justify-end gap-2 pt-2" data-id="element-3352" data-mp="3sr5zwax1yvtb">
                                    <button type="button" onClick={() => setCreatingAction(false)} className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 rounded-md transition-colors" data-id="element-3353" data-mp="l0uyk9fmozl">
                                      Cancel
                                    </button>
                                    <button type="submit" className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors" data-id="element-3354" data-mp="3duostvvwc3ck">
                                      Save Action
                                    </button>
                                  </div>
                                </form>}
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 4: Contractor Contacts */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3355" data-mp="2fzkqnc7r7u2o">
                    <button onClick={() => toggleSection('contacts')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3356" data-mp="2uwv955ite07w">
                      <div className="flex items-center gap-3" data-id="element-3357" data-mp="10iknt71fho6v">
                        <User size={18} className="text-gray-500" data-id="element-3358" data-mp="vjznz8q396iy" />
                        <h3 className="font-bold text-gray-900" data-id="element-3359" data-mp="376kx4m6vnqkw">
                          Contractor Contacts
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3360" data-mp="3c4utv1892z8m">
                        {reviewedSections.has('contacts') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3361" data-mp="32futbde3nvsb" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3362" data-mp="2oify1s160tsf" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('contacts') ? 'rotate-180' : ''}`} data-id="element-3363" data-mp="2ei5nkf6im0gy" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3364" data-mp="copxbjecanmi">
                      {openSections.has('contacts') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3365" data-mp="hn9i3gh75tda">
                          <div className="p-5 border-t border-gray-200 grid grid-cols-2 gap-3" data-id="element-3366" data-mp="yz7q3405nbg9">
                            {riskArea.contacts.map((contact, index) => <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-3367" data-mp="p94xv60uy1iz" data-mpi={index}>
                                <p className="font-semibold text-gray-900" data-id="element-3368" data-mp="1hp5wlfob0whs">
                                  {contact.name}
                                </p>
                                <p className="text-xs text-gray-500 mb-2" data-id="element-3369" data-mp="1cxefdvr1g34b">
                                  {contact.role} · {contact.company}
                                </p>
                                <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors" data-id="element-3370" data-mp="1p9q9d7mcg0xf">
                                  <Phone size={14} data-id="element-3371" data-mp="2470u77kvmj5t" />
                                  {contact.phone}
                                </a>
                              </div>)}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Section 5: AI Chat */}
                  <div className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm" data-id="element-3372" data-mp="2mxiaudsfpoat">
                    <button onClick={() => toggleSection('aiChat')} className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-3373" data-mp="2rwv71p5747q2">
                      <div className="flex items-center gap-3" data-id="element-3374" data-mp="389rwo7kmvigl">
                        <Sparkles size={18} className="text-indigo-500" data-id="element-3375" data-mp="3m6dniym3071i" />
                        <h3 className="font-bold text-gray-900" data-id="element-3376" data-mp="3h7u5386p5ddw">
                          Ask AI About This Risk
                        </h3>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-3377" data-mp="2tgevqvgi5vt5">
                        {reviewedSections.has('aiChat') ? <CheckCircle2 size={18} className="text-emerald-500" data-id="element-3378" data-mp="33946tp8557ny" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-3379" data-mp="2as088i5e8zx3" />}
                        <ChevronDown size={18} className={`text-gray-400 transition-transform ${openSections.has('aiChat') ? 'rotate-180' : ''}`} data-id="element-3380" data-mp="25tyn7roeqqiz" />
                      </div>
                    </button>
                    <AnimatePresence data-id="element-3381" data-mp="caq1ll84h10t">
                      {openSections.has('aiChat') && <motion.div initial={{
                    height: 0
                  }} animate={{
                    height: 'auto'
                  }} exit={{
                    height: 0
                  }} className="overflow-hidden" data-id="element-3382" data-mp="h9b1tkndjkp9">
                          <div className="p-5 border-t border-gray-200" data-id="element-3383" data-mp="zyzj4ayaz2m5">
                            <RiskAIChat context="specific" riskArea={riskArea} data-id="element-3384" data-mp="3fqncjwe3yd07" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div className="border-t border-gray-200 pt-6" data-id="element-3385" data-mp="706sdps2uqt4">
                {!activeForm ? <div className="flex flex-col gap-3" data-id="element-3386" data-mp="byqg9vq8eco4">
                    {riskArea.status === 'open' && <>
                        <button onClick={() => setActiveForm('measures')} className="w-full py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2" data-id="element-3387" data-mp="3ahyrdo4msb0t">
                          <Clock size={18} data-id="element-3388" data-mp="3p736uxk1k158" />
                          Mark Measures Taken
                        </button>
                        <button onClick={() => setActiveForm('close')} disabled={!allReviewed} className={`w-full py-3 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${allReviewed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3389" data-mp="2ro1s5546xepn">
                          <CheckCircle2 size={18} data-id="element-3390" data-mp="14v9eq5zwxk4t" />
                          {allReviewed ? 'Close Risk' : `Review ${remainingToReview} more section${remainingToReview === 1 ? '' : 's'} to close`}
                        </button>
                      </>}

                    {riskArea.status === 'measures_taken' && <>
                        <button onClick={() => setActiveForm('close')} disabled={!allReviewed} className={`w-full py-3 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${allReviewed ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-3391" data-mp="h4dp9fixj10q">
                          <CheckCircle2 size={18} data-id="element-3392" data-mp="2hf05r7joip6" />
                          {allReviewed ? 'Close Risk' : `Review ${remainingToReview} more section${remainingToReview === 1 ? '' : 's'} to close`}
                        </button>
                        <button onClick={() => setActiveForm('reopen')} className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2" data-id="element-3393" data-mp="g8tli4oxcvze">
                          <RotateCcw size={18} data-id="element-3394" data-mp="bhl70u8qmkfd" />
                          Reopen Risk
                        </button>
                      </>}

                    {riskArea.status === 'closed' && <button onClick={() => setActiveForm('reopen')} className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2" data-id="element-3395" data-mp="13z5znxarjoyf">
                        <RotateCcw size={18} data-id="element-3396" data-mp="2yem9wy4vh60c" />
                        Reopen Risk
                      </button>}
                  </div> : <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="space-y-4" data-id="element-3397" data-mp="25pf9ftluy6cg">
                    <div className="flex items-center gap-2 mb-4" data-id="element-3398" data-mp="2anz18an4uccq">
                      {activeForm === 'measures' && <Clock className="text-amber-500" size={20} data-id="element-3399" data-mp="3r54n4rzb8nrh" />}
                      {activeForm === 'close' && <CheckCircle2 className="text-emerald-500" size={20} data-id="element-3400" data-mp="1vnd3dn4flm6l" />}
                      {activeForm === 'reopen' && <RotateCcw className="text-gray-500" size={20} data-id="element-3401" data-mp="20o0r9zxd5mwl" />}
                      <h4 className="font-bold text-gray-900" data-id="element-3402" data-mp="24pm97zor8rdn">
                        {activeForm === 'measures' && 'Record Measures Taken'}
                        {activeForm === 'close' && 'Close Risk Area'}
                        {activeForm === 'reopen' && 'Reopen Risk Area'}
                      </h4>
                    </div>

                    <div data-id="element-3403" data-mp="2oaz2oj6mm4nn">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3404" data-mp="2ock0qis5g5q9">
                        Your Name
                      </label>
                      <input type="text" value={userName} onChange={e => setUserName(e.target.value)} placeholder="Enter your name" className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent ${activeForm === 'measures' ? 'focus:ring-amber-500' : activeForm === 'close' ? 'focus:ring-emerald-500' : 'focus:ring-gray-500'}`} data-id="element-3405" data-mp="2xc70l058d3h7" />
                    </div>
                    <div data-id="element-3406" data-mp="2yg7amdehv1cz">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3407" data-mp="2yh0cp12id2os">
                        {activeForm === 'measures' && 'What measures were taken?'}
                        {activeForm === 'close' && 'Resolution Notes'}
                        {activeForm === 'reopen' && 'Reason for reopening'}
                      </label>
                      <textarea value={formNotes} onChange={e => setFormNotes(e.target.value)} placeholder="Add details..." rows={3} className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none ${activeForm === 'measures' ? 'focus:ring-amber-500' : activeForm === 'close' ? 'focus:ring-emerald-500' : 'focus:ring-gray-500'}`} data-id="element-3408" data-mp="3hd8kocmwnrns" />
                    </div>
                    <div className="flex gap-3" data-id="element-3409" data-mp="1xg9uznw9qs8t">
                      <button onClick={resetForm} className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors" data-id="element-3410" data-mp="22g5mworoyt80">
                        Cancel
                      </button>
                      <button onClick={() => {
                  if (activeForm === 'measures') handleSubmitStatusUpdate('measures_taken');else if (activeForm === 'close') handleSubmitStatusUpdate('closed');else if (activeForm === 'reopen') handleSubmitStatusUpdate('open');
                }} disabled={!formNotes.trim() || !userName.trim()} className={`flex-1 py-2.5 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${activeForm === 'measures' ? 'bg-amber-500 hover:bg-amber-600' : activeForm === 'close' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-800 hover:bg-gray-900'}`} data-id="element-3411" data-mp="1ekbde10twcg1">
                        Confirm
                      </button>
                    </div>
                  </motion.div>}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-500 relative" data-id="element-3412" data-mp="1pyuxkjh23bbp">
                <div className="flex items-center gap-4" data-id="element-3413" data-mp="kgcqa95s8lh">
                  <span data-id="element-3414" data-mp="3rq7zr0rn3542">
                    Last updated: {riskArea.lastUpdated.toLocaleString()}
                  </span>
                  <span data-id="element-3415" data-mp="35vr7ofcixl88">Risk ID: {riskArea.id}</span>
                </div>

                <div data-id="element-3416" data-mp="3avlzcl44ieuc">
                  <button onClick={() => setShowGeneralFeedback(!showGeneralFeedback)} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors" data-id="element-3417" data-mp="3auu1t4qyu71f">
                    <MessageCircle size={12} data-id="element-3418" data-mp="3kkx2lr4bwpab" />
                    Send Feedback
                  </button>

                  <AnimatePresence data-id="element-3419" data-mp="2n016gucf0sve">
                    {showGeneralFeedback && <motion.div initial={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: 10,
                  scale: 0.95
                }} className="absolute bottom-full right-4 mb-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50" data-id="element-3420" data-mp="2rx9p0w1h6yx6">
                        <h4 className="font-semibold text-gray-900 mb-2 text-sm" data-id="element-3421" data-mp="1rvcuxohvxn7k">
                          Send Feedback
                        </h4>
                        <select value={feedbackType} onChange={e => setFeedbackType(e.target.value)} className="w-full text-xs p-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 mb-2" data-id="element-3422" data-mp="1hz9xgvxzacqy">
                          <option data-id="element-3423" data-mp="140tgmc6ej5v3">Risk assessment feedback</option>
                          <option data-id="element-3424" data-mp="33cmiam44wze5">AI response feedback</option>
                          <option data-id="element-3425" data-mp="33a06ube4hzqn">Feature suggestion</option>
                          <option data-id="element-3426" data-mp="2ydt1oktjws9r">Other</option>
                        </select>
                        <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} placeholder="Tell us more..." className="w-full text-xs p-2 border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none mb-2" rows={3} data-id="element-3427" data-mp="13krat7lih313" />
                        <div className="flex justify-end gap-2" data-id="element-3428" data-mp="1mgher0d089hm">
                          <button onClick={() => setShowGeneralFeedback(false)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1" data-id="element-3429" data-mp="1ii1h82jaripy">
                            Cancel
                          </button>
                          <button onClick={submitGeneralFeedback} disabled={!feedbackText.trim()} className="text-xs bg-gray-900 text-white px-3 py-1 rounded hover:bg-gray-800 disabled:opacity-50" data-id="element-3430" data-mp="23rsv8f21tz4">
                            Submit
                          </button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* PTP Document Viewer Modal */}
      <PTPDocumentViewer document={selectedPTPDocument} onClose={() => setSelectedPTPDocument(null)} data-id="element-3431" data-mp="5skpwpoqfgil" />

      <Toast message={toastMessage} isVisible={showToast} onClose={() => setShowToast(false)} data-id="element-3432" data-mp="fnur5b7b3jvb" />
    </>;
}